/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useElementClientPosition } from "@/composition/useElementClientPosition"
import { identity } from "@vueuse/core"
import { css } from "vite-css-in-js"
import {
	computed,
	nextTick,
	onMounted,
	ref,
	shallowRef,
	watch,
	type HTMLAttributes,
	type InputHTMLAttributes,
	type StyleValue,
} from "vue"
import {
	defineComponent,
	inferProps,
	positiveMod,
	propsWithDefaults,
	refSetter,
	useOnInput,
	type ReactiveComponent,
} from "vue-utils"
import createVirtualDropdownBox from "./VirtualDropdownBox"

interface Props {
	value: string
	setValue(value: string): void

	options: string[]
	noOptionsText: string | null
	onOptionSelected?(option: string): void

	/** @default true */
	openOnFocus?: boolean

	/** @default false */
	openOnMount?: boolean

	containerAttrs?: HTMLAttributes
	dropdownAttrs?: HTMLAttributes
}

const searchStyles = css`
	position: relative;

	input {
		width: 100%;
		height: 100%;
	}
`

const Dropdown = createVirtualDropdownBox<string>()
const AutoCompleteTextInput: ReactiveComponent<Props, InputHTMLAttributes> = (initialProps, { attrs }) => {
	const props = propsWithDefaults(initialProps, {
		openOnFocus: true,
		openOnMount: false,
	})

	const inputRef = shallowRef<HTMLInputElement>()
	const hoveredOption = ref<string | null>(null)
	const focussed = ref(false)

	const dropdownVisible = computed(() => {
		if (visibleOptions.value.length === 0 && !props.noOptionsText) {
			return false
		}
		return props.options.length > 0 && focussed.value && !attrs.readonly
	})
	const { bounds: inputPosition, refresh: refreshPosition } = useElementClientPosition(inputRef)

	const visibleOptions = computed(() => {
		const searchLower = props.value.toLocaleLowerCase()
		if (searchLower.trim().length === 0) {
			return props.options
		}
		return props.options.filter((o) => o.toLocaleLowerCase().includes(searchLower))
	})

	function handleOnInput(text: string) {
		props.setValue(text)
		focussed.value = true
	}

	function selectOption(option: string, focus = true) {
		props.setValue(option)
		props.onOptionSelected?.(option)
		focussed.value = false

		if (focus) {
			inputRef.value?.focus()
		}
	}

	function handleInputKeyDown(e: KeyboardEvent) {
		if (!dropdownVisible.value) {
			return
		}
		if ((e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) && hoveredOption.value) {
			if (e.key === "Enter") {
				e.preventDefault()
			}
			selectOption(hoveredOption.value, e.key === "Enter")
			return
		}

		if (e.key === "ArrowDown" || e.key === "ArrowUp") {
			e.preventDefault()
			moveOption(e.key === "ArrowDown" ? "down" : "up")
			return
		}
	}

	function moveOption(direction: "up" | "down") {
		const options = visibleOptions.value
		if (options.length === 0) {
			return
		}

		const selectedIndex = hoveredOption.value === null ? -1 : options.indexOf(hoveredOption.value)
		let newIndex
		if (direction === "down") {
			newIndex = selectedIndex + 1
		} else {
			newIndex = Math.max(0, selectedIndex) - 1
		}
		hoveredOption.value = options.at(positiveMod(newIndex, options.length)) ?? null
	}

	watch(dropdownVisible, (isVisible) => {
		if (!isVisible) {
			hoveredOption.value = null
		}
	})
	watch(visibleOptions, (newOptions) => {
		if (hoveredOption.value !== null && !newOptions.includes(hoveredOption.value)) {
			hoveredOption.value = null
		}
	})

	onMounted(() => {
		if (props.openOnMount) {
			inputRef.value?.focus()
			focussed.value = true
		}
	})

	function getDropdownPositionStyles(): StyleValue {
		const expectedHeight = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) * 18
		const expectedBottom = inputPosition.value.bottom + expectedHeight

		if (expectedBottom >= window.innerHeight * 0.95) {
			return {
				maxHeight: "18rem",
				bottom: "100%",
				left: 0,
				right: 0,
				borderRadius: "0.35rem 0.35rem 0 0",
				boxShadow: "0.25rem 0 0.25rem #a0a0a0",
			}
		} else {
			return {
				maxHeight: "18rem",
				position: "fixed",
				left: `${inputPosition.value.left}px`,
				width: `${inputPosition.value.width}px`,
				top: `${inputPosition.value.bottom}px`,
				borderRadius: "0 0 0.35rem 0.35rem",
				boxShadow: "0.25rem 0.25rem 0.25rem #a0a0a0",
			}
		}
	}

	function renderOption(option: string) {
		const search = props.value.toLowerCase()
		if (props.value.length === 0) {
			return option
		}
		const components = []
		let text = option
		while (text.length > 0) {
			const index = text.toLowerCase().indexOf(search)
			if (index < 0) {
				components.push(text)
				break
			}
			const firstPart = text.substring(0, index)
			if (firstPart.length > 0) {
				components.push(firstPart)
			}
			const matching = text.substring(index, index + search.length)
			components.push(<span style={{ color: "#0a6b0a" }}>{matching}</span>)
			text = text.substring(index + search.length)
		}
		return components
	}

	return () => (
		<div class={searchStyles} {...props.containerAttrs}>
			<input
				ref={inputRef}
				type="text"
				autocomplete="off"
				value={props.value}
				{...attrs}
				onInput={useOnInput(handleOnInput)}
				onFocus={() => {
					if (props.openOnFocus) {
						refreshPosition()
						focussed.value = true
					}
				}}
				onKeydown={handleInputKeyDown}
				onBlur={() => void nextTick(() => (focussed.value = false))}
			/>
			{dropdownVisible.value && (
				<Dropdown
					options={visibleOptions.value}
					getKey={identity}
					getTitle={identity}
					renderOption={renderOption}
					noOptionsText={props.noOptionsText}
					selectOption={selectOption}
					highlightedOption={hoveredOption.value}
					setHighlightedOption={refSetter(hoveredOption)}
					style={getDropdownPositionStyles()}
					{...props.dropdownAttrs}
				/>
			)}
		</div>
	)
}

export default defineComponent(AutoCompleteTextInput, inferProps<Props>())
